import { useTranslation } from 'react-i18next';
import { LegalLayout, Section, Bullets, Bullet } from './LegalLayout';

export function PrivacyPage() {
  const { t } = useTranslation();
  return (
    <LegalLayout
      eyebrow={t('privacy.eyebrow', { defaultValue: 'Privacy' }) as string}
      title={t('privacy.title', { defaultValue: 'Privacy Policy' })}
      updated="2026-05-23"
    >
      <Section id="overview" kicker="01" title={t('privacy.s1.title', { defaultValue: 'In one paragraph' }) as string}>
        <p>
          {t('privacy.s1.body', {
            defaultValue:
              'Knowlez is an enterprise knowledge platform. Your workspace owner is the data controller for everything indexed into the workspace; Knowlez Inc. is a processor acting on the controller\'s instructions. We collect the minimum data required to run the product, never sell it to anyone, never train models on it, and surface every retention and deletion control to the workspace owner.',
          })}
        </p>
      </Section>

      <Section id="who" kicker="02" title={t('privacy.s2.title', { defaultValue: 'Who we are' }) as string}>
        <p>
          {t('privacy.s2.body', {
            defaultValue:
              'Knowlez Inc. operates knowlez.com and the related cloud product. We can be reached at privacy@knowlez.com for any question about your data. Our data protection contact handles APPI, GDPR, and CCPA requests in any of the languages the product supports.',
          })}
        </p>
      </Section>

      <Section id="data" kicker="03" title={t('privacy.s3.title', { defaultValue: 'What we collect, and why' }) as string}>
        <p>{t('privacy.s3.intro', { defaultValue: 'Three buckets, with different rules for each:' })}</p>
        <Bullets>
          <Bullet>
            <strong>Account data</strong> — your name, email, password hash (bcrypt at 12 rounds, never the plaintext), country code, locale. Used to sign you in and route you to the right region. Stored as long as your account is active; deleted within 30 days of account closure.
          </Bullet>
          <Bullet>
            <strong>Workspace content</strong> — documents you upload, documents pulled from connected systems (Google Drive, M365, Slack, Notion, …), the chunks we extract from them, embeddings and indexes, conversation history with the AI agents. Belongs to the workspace owner. Retained for the period the owner configures (default 2 years; Enterprise tier supports up to 7 years for J-SOX evidence). The owner can delete any document at any time; deletion is propagated to Postgres, Qdrant, the lexical index, and the blob store within 24 hours.
          </Bullet>
          <Bullet>
            <strong>Operational telemetry</strong> — request logs, error traces, AI cost ledger (`ai_runs` table — model, tokens, cost, retrieved + cited chunk IDs). Kept 90 days for debugging and billing dispute resolution.
          </Bullet>
        </Bullets>
        <p className="mt-3">
          {t('privacy.s3.never', {
            defaultValue:
              "We never sell your data. We never share it with advertisers. We do not train AI models on your workspace content — we route AI calls through our model gateway using your workspace's chosen tier, and the request never contributes to model training (every supported provider's API terms guarantee this for paid API traffic).",
          })}
        </p>
      </Section>

      <Section id="ai" kicker="04" title={t('privacy.s4.title', { defaultValue: 'How AI sees your content' }) as string}>
        <p>
          {t('privacy.s4.body', {
            defaultValue:
              'When you ask a question, Knowlez runs hybrid retrieval (vector + lexical + graph) against your workspace index, picks the top-K chunks you are permitted to see, and sends those chunks + your question to the AI tier configured for your workspace. The model returns an answer with citations back to the chunks. Every call is recorded in `ai_runs` with the model tier, prompt and completion tokens, cost in cents, retrieved chunk IDs, and cited chunk IDs. You can see every run on the Settings page.',
          })}
        </p>
      </Section>

      <Section id="connectors" kicker="05" title={t('privacy.s5.title', { defaultValue: 'Connectors and source-system permissions' }) as string}>
        <p>
          {t('privacy.s5.body', {
            defaultValue:
              "When you connect Google Drive, M365, Slack, or any of the 14 supported sources, Knowlez requests the minimum scope needed to read what you've granted access to. We capture the source's permission graph (who can see each document) and re-check it at retrieval time, so a chunk visible only to one Slack channel never appears in an answer for someone outside that channel. OAuth tokens are encrypted at rest with AES-256-GCM. Revoke a connector at any time on the Connectors page; tokens are wiped immediately and no further sync occurs.",
          })}
        </p>
      </Section>

      <Section id="rights" kicker="06" title={t('privacy.s6.title', { defaultValue: 'Your rights (GDPR · APPI · CCPA)' }) as string}>
        <p>{t('privacy.s6.intro', { defaultValue: 'You can request, at any time:' })}</p>
        <Bullets>
          <Bullet>{t('privacy.s6.b1', { defaultValue: 'A copy of all personal data we hold about you.' })}</Bullet>
          <Bullet>{t('privacy.s6.b2', { defaultValue: 'Correction of any data that is inaccurate.' })}</Bullet>
          <Bullet>{t('privacy.s6.b3', { defaultValue: 'Deletion of your account and all associated personal data.' })}</Bullet>
          <Bullet>{t('privacy.s6.b4', { defaultValue: 'Export of your workspace content in a portable format.' })}</Bullet>
          <Bullet>{t('privacy.s6.b5', { defaultValue: 'A pause on processing while a dispute is resolved.' })}</Bullet>
        </Bullets>
        <p className="mt-3">
          {t('privacy.s6.contact', {
            defaultValue:
              'Email privacy@knowlez.com — we respond within 30 days. Workspace owners can fulfill most requests themselves from the Settings and Audit-log pages without contacting us.',
          })}
        </p>
      </Section>

      <Section id="subprocessors" kicker="07" title={t('privacy.s7.title', { defaultValue: 'Subprocessors' }) as string}>
        <p>{t('privacy.s7.intro', { defaultValue: 'We use a small set of vendors. Each is bound by a written data-processing agreement and audited annually:' })}</p>
        <Bullets>
          <Bullet><strong>Hetzner Online GmbH</strong> — hosting (Germany, Finland; Tokyo region for Global tier).</Bullet>
          <Bullet><strong>AI model gateway</strong> — routing for AI calls across supported providers.</Bullet>
          <Bullet><strong>Cloudflare Inc.</strong> — DNS, CDN, geo-detection, DDoS protection.</Bullet>
          <Bullet><strong>Stripe Inc.</strong> — billing (Growth / Global / Enterprise plans).</Bullet>
          <Bullet><strong>Postmark / Amazon SES</strong> — transactional email.</Bullet>
        </Bullets>
        <p className="mt-3">
          {t('privacy.s7.notice', {
            defaultValue: 'On-prem customers have zero subprocessors — the entire stack runs inside your VPC or air-gap.',
          })}
        </p>
      </Section>

      <Section id="security" kicker="08" title={t('privacy.s8.title', { defaultValue: 'Security' }) as string}>
        <p>
          {t('privacy.s8.body', {
            defaultValue:
              'TLS 1.2+ in transit. AES-256-GCM at rest for OAuth tokens, SSO secrets, and SCIM bearer tokens. bcrypt at 12 rounds for passwords. Audit-log writes on every mutating action. Source-system ACLs re-checked at retrieval. JIS X 8341 and WCAG 2.2 AA accessibility from day one. See the Security page for the full posture.',
          })}
        </p>
      </Section>

      <Section id="children" kicker="09" title={t('privacy.s9.title', { defaultValue: 'Children' }) as string}>
        <p>
          {t('privacy.s9.body', {
            defaultValue:
              'Knowlez is a B2B product. We do not knowingly collect data from anyone under 16. If you believe a child has provided us with personal data, email privacy@knowlez.com and we will delete it.',
          })}
        </p>
      </Section>

      <Section id="changes" kicker="10" title={t('privacy.s10.title', { defaultValue: 'Changes to this policy' }) as string}>
        <p>
          {t('privacy.s10.body', {
            defaultValue:
              'Material changes are announced in-product 30 days before they take effect. The history of every revision lives in the public Knowlez changelog.',
          })}
        </p>
      </Section>
    </LegalLayout>
  );
}
